import TitrePage from "../components/charte/TitrePage";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box, Typography, Button, TextField, MenuItem, Select, FormControl,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  Chip, IconButton, Switch, Tooltip,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";

import { loaderOff, loaderOn, openSnackbar } from "../redux/actions";
import {
  Grille, Breakdown, LIBELLE_FACTEUR, TarifDeBase,
  getGrilles, clonerGrille, activerGrille, modifierGrille, supprimerGrille,
  modifierCoefficient, modifierTarif, creerTarifFin, supprimerTarif,
  simuler, expliquerCoefficient, messageErreur,
  MetriquesPricing, MetriquesQuetes, getMetriques, getMetriquesQuetes,
} from "../utilities/pricingService";
import { getTypeLieuCategories, getSousTypes, TypeLieuCategorie, TypeLieuSousType } from "../utilities/typeLieuService";
import { getConfigActive, majReglagesArgent } from "../utilities/adminservice";
import { estSuperAdmin } from "../api/role";
import CommonDialog from "../commondialog/confirm";
import { AIDE_REGLAGES } from "../aide/textesAide";

/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  card: "var(--c-surface)", card2: "var(--c-surface-2)", border: "var(--c-border)",
  accent: "#1F1E1B", accentLight: "#57534A", accentMuted: "rgba(31,30,27,0.07)",
  text: "#1F1E1B", textSub: "#57534A", textMuted: "#57534A",
  success: "#356A0F", warning: "#7C5600", error: "#A23A0D",
} as const;

const champSx = {
  "& .MuiOutlinedInput-root": {
    background: C.card2, color: C.text, borderRadius: "8px",
    "& fieldset": { borderColor: C.border },
    "&:hover fieldset": { borderColor: C.accentLight },
  },
  "& .MuiInputLabel-root": { color: C.textSub, fontSize: 13 },
};

/** Libellés des champs d'un tarif de base, tels qu'affichés dans le tableau. */
const LIBELLE_CHAMP_TARIF = {
  baseRewardTips: "Récompense Spotter (Tips)",
  baseCostCfa: "Plancher client (F CFA)",
} as const;

const CHAMPS = ["nom", "adresse", "telephone", "photo", "prix", "gps", "horaires", "contact"];

/* Les valeurs proposées par le simulateur VIENNENT DE LA GRILLE SIMULÉE, elles
   ne sont pas écrites ici. Une liste en dur se serait désynchronisée du jour
   où une grille renomme une clé ou en ajoute une — et on simulerait alors des
   cas que le moteur ne reconnaît pas, en obtenant des résultats faux sans
   comprendre pourquoi. */
function clesDuFacteur(grille: Grille | null, facteur: string): string[] {
  return (grille?.coefficients ?? [])
    .filter(c => c.facteur === facteur)
    .map(c => c.cle);
}

/**
 * Administration du moteur de pricing.
 *
 * TROIS PRINCIPES, qui expliquent la forme de l'écran :
 *
 * 1. Chaque case dit ce qu'elle fait. Un coefficient n'est jamais affiché
 *    seul : une phrase en dessous dit ce qu'il multiplie, dans quel sens, et
 *    sur quel montant. « ×1,4 sur urgence » ne veut rien dire.
 *
 * 2. On voit l'effet avant de décider. Le simulateur appelle le moteur sur la
 *    grille en cours d'édition et rend le calcul détaillé. Remplir des
 *    coefficients sans voir ce qu'ils produisent, c'est le problème qu'on
 *    corrige, pas une façon de le corriger.
 *
 * 3. Une grille active est figée. On clone, on ajuste, on simule, on active.
 *    Le serveur refuse toute écriture sur la grille active — l'écran ne fait
 *    que refléter cette règle.
 */
export default function Pricing() {
  const dispatch = useDispatch();

  const [grilles, setGrilles] = useState<Grille[]>([]);
  const [selection, setSelection] = useState<number | null>(null);
  const [categoriesRef, setCategoriesRef] = useState<TypeLieuCategorie[]>([]);
  const categories = useMemo(() => categoriesRef.map(c => c.libelle), [categoriesRef]);
  const [sousTypes, setSousTypes] = useState<TypeLieuSousType[]>([]);

  /* ── Simulateur ── */
  const [sim, setSim] = useState({
    categoryMain: "", categorySub: "", zone: "", urgence: "",
    exclusiviteClient: "", deficitInformation: "", champs: ["nom", "photo"] as string[],
    collectesAttendues: "50", canal: "",
  });
  const [resultat, setResultat] = useState<Breakdown | null>(null);
  const [metriques, setMetriques] = useState<MetriquesPricing | null>(null);
  const [metriquesQuetes, setMetriquesQuetes] = useState<MetriquesQuetes | null>(null);
  const [aActiver, setAActiver] = useState<Grille | null>(null);
  /* Suppressions confirmées : un brouillon ou un tarif fin supprimé ne se
     récupère pas. */
  const [aSupprimer, setASupprimer] = useState<Grille | null>(null);
  const [tarifASupprimer, setTarifASupprimer] = useState<TarifDeBase | null>(null);
  /* Cases de tarif laissées vides : signalées, jamais envoyées. */
  const [tarifsVides, setTarifsVides] = useState<Set<string>>(new Set());

  /* Forfait de repli des collectes libres : lu sur la configuration active
     d'admin-service, modifiable par un SUPER_ADMIN seulement (décision D4). */
  const superAdmin = estSuperAdmin();
  const [forfaitSauvage, setForfaitSauvage] = useState<number | null>(null);
  const [forfaitSaisi, setForfaitSaisi] = useState("");
  const [motifForfait, setMotifForfait] = useState("");
  const chargerForfait = useCallback(async () => {
    const c: any = await getConfigActive();
    const v = typeof c?.tipsParCollecteSauvage === "number" ? c.tipsParCollecteSauvage : null;
    setForfaitSauvage(v);
    setForfaitSaisi(v != null ? String(v) : "");
  }, []);
  useEffect(() => { chargerForfait(); }, [chargerForfait]);

  const enregistrerForfait = async () => {
    const n = parseInt(forfaitSaisi, 10);
    if (isNaN(n) || n < 1) {
      dispatch(openSnackbar("Le forfait doit être un nombre entier de Tips, au moins 1.", "red"));
      return;
    }
    if (!motifForfait.trim()) {
      dispatch(openSnackbar("Le motif est obligatoire.", "red"));
      return;
    }
    try {
      await majReglagesArgent({ tipsParCollecteSauvage: n, motif: motifForfait.trim() });
      dispatch(openSnackbar("Forfait enregistré.", "green"));
      setMotifForfait("");
      chargerForfait();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Enregistrement du forfait impossible."), "red"));
    }
  };

  const charger = useCallback(async () => {
    dispatch(loaderOn());
    try {
      const [g, cats, sous] = await Promise.all([
        getGrilles(), getTypeLieuCategories(), getSousTypes(undefined, true),
      ]);
      setGrilles(g);
      setCategoriesRef(cats);
      setSousTypes(sous);
      setSelection(prev => prev ?? (g.find(x => x.ruleset.actif)?.ruleset.id ?? g[0]?.ruleset.id ?? null));
      setSim(s => s.categoryMain ? s : { ...s, categoryMain: cats[0]?.libelle ?? "" });
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Impossible de charger les grilles."), "red"));
    } finally {
      dispatch(loaderOff());
    }
  }, [dispatch]);

  useEffect(() => { charger(); }, [charger]);

  /* Les métriques croisent les deux services : le moteur sait ce qu'il a
     proposé, les quêtes savent si les missions se sont pourvues. */
  useEffect(() => {
    let vivant = true;
    (async () => {
      const q = await getMetriquesQuetes();
      if (!vivant) return;
      setMetriquesQuetes(q);
      try {
        const m = await getMetriques(q?.missionsConfrontees ?? 0);
        if (vivant) setMetriques(m);
      } catch { /* le tableau de bord est informatif, il ne bloque rien */ }
    })();
    return () => { vivant = false; };
  }, []);

  const grille = useMemo(
    () => grilles.find(g => g.ruleset.id === selection) ?? null, [grilles, selection]);
  const figee = !!grille?.ruleset.actif;

  const zones        = useMemo(() => clesDuFacteur(grille, "zone"), [grille]);
  const urgences     = useMemo(() => clesDuFacteur(grille, "urgence"), [grille]);
  const exclusivites = useMemo(() => clesDuFacteur(grille, "exclusivite"), [grille]);
  const deficits     = useMemo(() => clesDuFacteur(grille, "deficit"), [grille]);

  /* La grille change : les clés retenues doivent redevenir valides, sinon on
     simulerait avec une zone que cette grille ne connaît pas. */
  useEffect(() => {
    if (!grille) return;
    setSim(s => ({
      ...s,
      zone: zones.includes(s.zone) ? s.zone : (zones[0] ?? ""),
      urgence: urgences.includes(s.urgence) ? s.urgence : (urgences[0] ?? ""),
      exclusiviteClient: exclusivites.includes(s.exclusiviteClient)
        ? s.exclusiviteClient : (exclusivites[0] ?? ""),
      deficitInformation: deficits.includes(s.deficitInformation)
        ? s.deficitInformation : (deficits[0] ?? ""),
    }));
  }, [grille, zones, urgences, exclusivites, deficits]);

  const parFacteur = useMemo(() => {
    const m: Record<string, Grille["coefficients"]> = {};
    (grille?.coefficients ?? []).forEach(c => { (m[c.facteur] ||= []).push(c); });
    return m;
  }, [grille]);

  const agir = async (action: () => Promise<unknown>, succes: string) => {
    try {
      await action();
      dispatch(openSnackbar(succes, "green"));
      charger();
    } catch (err: any) {
      dispatch(openSnackbar(messageErreur(err, "Opération impossible."), "red"));
    }
  };

  const lancerSimulation = async () => {
    if (!grille) return;
    try {
      setResultat(await simuler({
        canal: sim.canal,
        rulesetVersion: grille.ruleset.version,   // on simule la grille affichée
        categoryMain: sim.categoryMain,
        categorySub: sim.categorySub || null,
        champsCollecte: sim.champs,
        collectesAttendues: parseInt(sim.collectesAttendues) || 1,
        zone: sim.zone,
        urgence: sim.urgence,
        exclusiviteClient: sim.exclusiviteClient,
        deficitInformation: sim.deficitInformation,
      }));
    } catch (err: any) {
      setResultat(null);
      dispatch(openSnackbar(messageErreur(err, "Simulation impossible."), "red"));
    }
  };

  const fr = (n: number) => n.toLocaleString("fr-FR");

  return (
    <Box sx={{ p: { xs: 2, md: 3.5 } }}>
      <TitrePage domaine="reglages" aide={AIDE_REGLAGES.moteurPrix}>Moteur de prix</TitrePage>
      <Typography sx={{ fontSize: 13, color: C.textSub, mb: 3 }}>
        Le moteur part de l'effort demandé au Spotter et en déduit le prix client par la marge.
        Une grille active est figée : clonez-la, ajustez le brouillon, simulez-le, puis activez-le.
      </Typography>

      {/* ── Ce que le moteur a produit, et ce qu'on en a fait ── */}
      {metriques && (
        <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 0.5 }}>
            Ce que le moteur a produit
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 2 }}>
            Tout est recalculé à la lecture, rien n'est stocké en double : une métrique figée
            diverge de la donnée qu'elle résume à la première correction.
          </Typography>

          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 2 }}>
            {[
              // Simulations et récompenses de collecte libre sont aussi journalisées :
              // elles ne comptent pas comme estimations (Z2-27).
              ["Estimations réelles", `${metriques.estimationsHorsSimulation}`,
                `dont ${metriques.estimationsAvecValeurRetenue} avec décision connue · hors `
                + `${metriques.simulations} simulations et ${metriques.recompensesCollecteLibre} collectes libres`, C.text],
              ["Missions confrontées", `${metriquesQuetes?.missionsConfrontees ?? 0}`,
                `sur ${metriquesQuetes?.avecInstantane ?? 0} avec instantané`, C.accentLight],
              ["Missions pourvues", metriquesQuetes?.tauxPourvuesPct != null
                ? `${metriquesQuetes.tauxPourvuesPct} %` : "—",
                "collectes réalisées ≥ attendues", C.success],
              ["Marge effective", metriquesQuetes?.margeEffectiveMoyennePct != null
                ? `${metriquesQuetes.margeEffectiveMoyennePct} %` : "—",
                "sur les collectes réalisées", C.warning],
            ].map(([titre, valeur, sous, couleur]) => (
              <Box key={titre as string}>
                <Typography sx={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {titre}
                </Typography>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: couleur as string }}>{valeur}</Typography>
                <Typography sx={{ fontSize: 11, color: C.textMuted }}>{sous}</Typography>
              </Box>
            ))}
          </Box>

          {/* L'ÉCART DANS LE TEMPS. Un écart isolé ne dit rien ; un écart
              systématiquement dans le même sens dit que la grille est fausse. */}
          <Box sx={{
            background: metriques.lectureDeLEcart.startsWith("⚠")
              ? "rgba(124,86,0,0.08)" : C.card2,
            border: `1px solid ${metriques.lectureDeLEcart.startsWith("⚠")
              ? "rgba(124,86,0,0.25)" : C.border}`,
            borderRadius: "8px", p: 2, mb: 2,
          }}>
            <Typography sx={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: "4px" }}>
              Écart entre ce que le moteur propose et ce que vous retenez
            </Typography>
            <Typography sx={{ fontSize: 12, color: C.text, lineHeight: 1.7, mb: 1 }}>
              {metriques.lectureDeLEcart}
            </Typography>
            {metriques.serieEcarts.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "flex-end", gap: "3px", height: 60, mt: 1 }}>
                {metriques.serieEcarts.map((pt, i) => {
                  const h = Math.min(28, Math.abs(pt.ecartPct) / 2);
                  return (
                    <Tooltip key={i} arrow title={
                      `${pt.date} — proposé ${pt.suggestion} Tips, retenu ${pt.retenu} `
                      + `(${pt.ecartPct > 0 ? "+" : ""}${pt.ecartPct} %)`}>
                      <Box sx={{ width: 14, display: "flex", flexDirection: "column",
                        justifyContent: "center", alignItems: "center", height: "100%" }}>
                        <Box sx={{ height: 28, display: "flex", alignItems: "flex-end" }}>
                          {pt.ecartPct > 0 && <Box sx={{ width: "100%", height: h, background: C.success, borderRadius: "2px 2px 0 0" }} />}
                        </Box>
                        <Box sx={{ width: "100%", height: "1px", background: C.border }} />
                        <Box sx={{ height: 28, display: "flex", alignItems: "flex-start" }}>
                          {pt.ecartPct < 0 && <Box sx={{ width: "100%", height: h, background: C.error, borderRadius: "0 0 2px 2px" }} />}
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
                <Typography sx={{ fontSize: 10, color: C.textMuted, ml: 1, alignSelf: "center" }}>
                  vers le haut = vous payez plus que proposé
                </Typography>
              </Box>
            )}
          </Box>

          {/* LA FIABILITÉ ET CE QUI LUI MANQUE. Un pourcentage sans cette
              explication se lit comme un verdict, alors que c'est un état
              d'avancement. */}
          <Box sx={{ background: C.card2, borderRadius: "8px", p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5, mb: 1 }}>
              <Typography sx={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Niveau de confiance
              </Typography>
              <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.accentLight }}>
                {metriques.fiabilite.pourcentage} %
              </Typography>
              <Chip size="small" label={metriques.fiabilite.niveauConfiance}
                sx={{ height: 20, fontSize: 10, background: C.accentMuted, color: C.accentLight }} />
            </Box>
            <Typography sx={{ fontSize: 11, color: C.textSub, mb: "6px" }}>
              Ce qui manque pour juger les suggestions du moteur fiables — le prix d'une mission reste fixé à la main :
            </Typography>
            {metriques.fiabilite.ceQuiManque.map((l, i) => (
              <Typography key={i} sx={{ fontSize: 11, color: C.textSub, lineHeight: 1.7 }}>· {l}</Typography>
            ))}
            <Typography sx={{ fontSize: 10, color: C.textMuted, mt: 1 }}>
              Formule : 60 % missions confrontées ({metriques.fiabilite.missionsConfrontees}/
              {metriques.fiabilite.missionsNecessaires}) + 40 % estimations avec décision connue
              ({metriques.fiabilite.estimationsAvecIssue}/{metriques.fiabilite.estimationsNecessaires}).
            </Typography>
          </Box>
        </Box>
      )}

      {/* ── Les grilles ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 1.5 }}>
          Grilles <span style={{ color: C.textMuted, fontWeight: 400 }}>({grilles.length})</span>
        </Typography>
        {grilles.map(g => (
          <Box key={g.ruleset.id} sx={{
            display: "flex", alignItems: "center", gap: 1, py: 0.75,
            borderBottom: `1px solid ${C.border}`,
          }}>
            <Box
              onClick={() => { setSelection(g.ruleset.id); setResultat(null); }}
              sx={{ flex: 1, cursor: "pointer" }}
            >
              <Typography sx={{
                fontSize: 13, color: g.ruleset.id === selection ? C.accentLight : C.text,
                fontWeight: g.ruleset.id === selection ? 700 : 400,
              }}>
                {g.ruleset.version}
              </Typography>
              {g.ruleset.commentaire && (
                <Typography sx={{ fontSize: 11, color: C.textMuted }}>{g.ruleset.commentaire}</Typography>
              )}
              {g.ruleset.activeeLe && (
                <Typography sx={{ fontSize: 11, color: C.textMuted }}>
                  {g.ruleset.actif ? "Activée" : "Dernière activation"} le{" "}
                  {new Date(g.ruleset.activeeLe).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                  {g.ruleset.activeePar ? ` par ${g.ruleset.activeePar}` : ""}
                </Typography>
              )}
            </Box>
            <Chip size="small" label={g.ruleset.actif ? "Active" : "Brouillon"}
              sx={{
                height: 22, fontSize: 11, fontWeight: 600,
                background: g.ruleset.actif ? "rgba(53,106,15,0.12)" : "rgba(87,83,74,0.15)",
                color: g.ruleset.actif ? C.success : C.textMuted,
              }} />
            <Tooltip title="Cloner en brouillon">
              <IconButton size="small"
                onClick={() => agir(() => clonerGrille(g.ruleset.id), "Brouillon créé.")}>
                <ContentCopyRoundedIcon sx={{ fontSize: 16, color: C.textSub }} />
              </IconButton>
            </Tooltip>
            {!g.ruleset.actif && (
              <>
                {/* Activer change toutes les estimations : réservé au
                    super-administrateur (le serveur refuse les autres, 403). */}
                <Tooltip title={superAdmin ? "" : "Réservé au super-administrateur"}>
                  <span>
                    <Button size="small" startIcon={<PlayArrowRoundedIcon />}
                      disabled={!superAdmin}
                      onClick={() => setAActiver(g)}
                      sx={{ textTransform: "none", fontSize: 12, color: C.success }}>
                      Activer
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Supprimer ce brouillon">
                  <IconButton size="small" onClick={() => setASupprimer(g)}>
                    <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        ))}
      </Box>

      <CommonDialog
        open={!!aActiver}
        handleClose={() => setAActiver(null)}
        handleConfirm={() => {
          const g = aActiver;
          setAActiver(null);
          if (g) agir(() => activerGrille(g.ruleset.id),
            `« ${g.ruleset.version} » est désormais la grille active.`);
        }}
        contentText={`Activer cette grille change toutes les estimations de prix à partir de maintenant.${
          aActiver ? ` Grille : « ${aActiver.ruleset.version} ».` : ""}`}
      />

      <CommonDialog
        open={!!aSupprimer}
        handleClose={() => setASupprimer(null)}
        handleConfirm={() => {
          const g = aSupprimer;
          setASupprimer(null);
          if (g) agir(() => supprimerGrille(g.ruleset.id), `Brouillon « ${g.ruleset.version} » supprimé.`);
        }}
        contentText={`Supprimer définitivement le brouillon${
          aSupprimer ? ` « ${aSupprimer.ruleset.version} »` : ""} ? Ses coefficients, tarifs et marges seront perdus.`}
      />

      <CommonDialog
        open={!!tarifASupprimer}
        handleClose={() => setTarifASupprimer(null)}
        handleConfirm={() => {
          const t = tarifASupprimer;
          setTarifASupprimer(null);
          if (t) agir(() => supprimerTarif(t.id), "Tarif fin supprimé.");
        }}
        contentText={`Supprimer le tarif fin${
          tarifASupprimer ? ` « ${tarifASupprimer.categoryMain} › ${tarifASupprimer.categorySub ?? ""} »` : ""} ? `
          + "Le sous-type retombera sur le tarif de sa catégorie."}
      />

      {grille && (
        <>
          {figee && (
            <Box sx={{
              background: "rgba(124,86,0,0.08)", border: "1px solid rgba(124,86,0,0.25)",
              borderRadius: "8px", px: 2, py: "10px", mb: 2,
            }}>
              <Typography sx={{ fontSize: 12, color: C.warning, fontWeight: 700 }}>
                « {grille.ruleset.version} » est la grille active : elle sert les estimations et ne se modifie pas.
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.textSub, mt: "2px" }}>
                Clonez-la pour préparer une évolution. Vous pouvez la simuler telle quelle ci-dessous.
              </Typography>
            </Box>
          )}

          {/* ── Réglages de la grille ── */}
          <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 0.5 }}>Réglages généraux</Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 2 }}>
              La marge est la part que Tribe garde sur le prix facturé. Les bornes limitent le PRODUIT
              des multiplicateurs : c'est leur empilement qui dérape, pas un facteur pris seul. Viennent
              ensuite les bornes de la récompense d'une mission et les seuils de couverture de zone.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {[
                { cle: "margeTribeDefaut", label: "Marge par défaut", aide: "0,30 = Tribe garde 30 % du prix facturé", pas: "0.01" },
                { cle: "plancherMultiplicateur", label: "Plancher du produit", aide: "les multiplicateurs ne peuvent pas descendre sous cette valeur", pas: "0.05" },
                { cle: "plafondMultiplicateur", label: "Plafond du produit", aide: "ni monter au-dessus", pas: "0.1" },
                // Z2-26 : jusqu'ici modifiables seulement en base.
                { cle: "plancherRecompenseTips", label: "Récompense minimale (Tips)", aide: "un Spotter ne touche jamais moins par collecte de mission", pas: "1" },
                { cle: "plafondRecompenseTips", label: "Récompense maximale (Tips)", aide: "ni plus que ce montant", pas: "1" },
                { cle: "plancherClientMajoration", label: "Majoration minimale du prix client", aide: "0,10 = jamais moins que le coût de la collecte + 10 %", pas: "0.01" },
                { cle: "seuilCouvertureBonne", label: "Seuil « zone bien couverte »", aide: "0,60 = au-delà de 60 % de couverture mesurée", pas: "0.05" },
                { cle: "seuilCouverturePartielle", label: "Seuil « zone partiellement couverte »", aide: "0,30 = à partir de 30 % ; en dessous, zone aveugle", pas: "0.05" },
              ].map(({ cle, label, aide, pas }) => (
                <Box key={cle} sx={{ minWidth: 230 }}>
                  <TextField
                    // Clé liée à la grille et à la valeur : le champ se recharge quand on
                    // change de grille ou après enregistrement (Z2-25).
                    key={`${grille.ruleset.id}-${cle}-${(grille.ruleset as any)[cle]}`}
                    size="small" fullWidth label={label} type="number" sx={champSx}
                    disabled={figee}
                    inputProps={{ step: pas }}
                    defaultValue={(grille.ruleset as any)[cle] ?? ""}
                    onBlur={e => {
                      // Le serveur valide (bornes, plancher ≤ plafond, seuils) et renvoie un message lisible.
                      const v = parseFloat(e.target.value);
                      if (!isNaN(v) && v !== (grille.ruleset as any)[cle]) {
                        agir(() => modifierGrille(grille.ruleset.id, { [cle]: v } as any), "Réglage enregistré.");
                      }
                    }}
                  />
                  <Typography sx={{ fontSize: 10, color: C.textMuted, mt: "3px" }}>{aide}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          {/* ── Collectes libres (hors mission) ── */}
          <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 0.5 }}>Collectes libres (hors mission)</Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 2 }}>
              Tips versés au Spotter pour chaque geste fait en dehors d'une mission, une fois le geste validé.
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {[
                { cle: "recompenseDecouverteTips", label: "Découverte", aide: "un lieu absent de la base, ajouté par le Spotter" },
                { cle: "recompenseValidationTips", label: "Validation", aide: "un lieu existant, confirmé sur place" },
                { cle: "recompenseMiseAJourTips", label: "Mise à jour", aide: "un lieu obsolète, corrigé par le Spotter" },
              ].map(({ cle, label, aide }) => (
                <Box key={cle} sx={{ minWidth: 230 }}>
                  <TextField
                    key={`${grille.ruleset.id}-${cle}-${(grille.ruleset as any)[cle]}`}
                    size="small" fullWidth label={`${label} (Tips)`} type="number" sx={champSx}
                    disabled={figee}
                    inputProps={{ step: "1", min: "1" }}
                    defaultValue={(grille.ruleset as any)[cle] ?? ""}
                    onBlur={e => {
                      const v = parseInt(e.target.value, 10);
                      if (isNaN(v) || v === (grille.ruleset as any)[cle]) return;
                      if (v < 1) {
                        dispatch(openSnackbar("Un geste rapporte au moins 1 Tip.", "red"));
                        return;
                      }
                      agir(() => modifierGrille(grille.ruleset.id, { [cle]: v } as any), "Réglage enregistré.");
                    }}
                  />
                  <Typography sx={{ fontSize: 10, color: C.textMuted, mt: "3px" }}>{aide}</Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${C.border}` }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.text }}>
                Forfait si le moteur est injoignable
              </Typography>
              <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 1.5 }}>
                Tips versés par collecte libre quand le moteur de pricing ne répond pas. Réglage de la
                configuration active, commun à toutes les grilles{superAdmin ? "" : " — modifiable par un super administrateur"}.
              </Typography>
              {superAdmin ? (
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                  <TextField size="small" type="number" label="Forfait (Tips)" sx={{ ...champSx, width: 150 }}
                    inputProps={{ step: "1", min: "1" }}
                    value={forfaitSaisi} onChange={e => setForfaitSaisi(e.target.value)} />
                  <TextField size="small" label="Motif (obligatoire)" sx={{ ...champSx, minWidth: 260 }}
                    value={motifForfait} onChange={e => setMotifForfait(e.target.value)} />
                  <Button size="small" variant="contained" onClick={enregistrerForfait}
                    disabled={!motifForfait.trim() || forfaitSaisi === String(forfaitSauvage ?? "")}
                    sx={{ textTransform: "none", background: C.accent }}>
                    Enregistrer
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ fontSize: 16, fontWeight: 700, color: C.accentLight }}>
                  {forfaitSauvage != null ? `${forfaitSauvage} Tips` : "—"}
                </Typography>
              )}
            </Box>
          </Box>

          {/* ── Coefficients ── */}
          <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 0.5 }}>Coefficients</Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 2 }}>
              Un coefficient à 1 ne fait rien. Au-dessus il augmente, en dessous il diminue.
              La phrase sous chaque case dit ce qui est touché — la récompense du Spotter, le prix client, ou les deux.
            </Typography>
            {Object.entries(parFacteur).map(([facteur, liste]) => (
              <Box key={facteur} sx={{ mb: 2 }}>
                <Typography sx={{
                  fontSize: 11, color: C.accentLight, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.06em", mb: "6px",
                }}>
                  {LIBELLE_FACTEUR[facteur] ?? facteur}
                </Typography>
                {liste.map(c => (
                  <Box key={c.id} sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: "8px" }}>
                    <TextField
                      key={`${grille.ruleset.id}-${c.id}-${c.multiplicateur}`}
                      size="small" type="number" sx={{ ...champSx, width: 110 }}
                      disabled={figee}
                      inputProps={{ step: "0.05", min: "0.05" }}
                      defaultValue={c.multiplicateur}
                      onBlur={e => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v > 0 && v !== c.multiplicateur) {
                          agir(() => modifierCoefficient(c.id, v), "Coefficient enregistré.");
                        }
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 12, color: C.text }}>{c.cle}</Typography>
                      <Typography sx={{
                        fontSize: 11, lineHeight: 1.5,
                        color: c.multiplicateur === 1 ? C.textMuted : C.textSub,
                      }}>
                        {expliquerCoefficient(c)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>

          {/* ── Tarifs de base ── */}
          <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: "8px", p: 2.5, mb: 3 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 0.5 }}>
              Tarifs de base <span style={{ color: C.textMuted, fontWeight: 400 }}>({grille.tarifsDeBase.length})</span>
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 2 }}>
              Le point de départ du calcul, avant tout multiplicateur. La récompense est ce que gagne
              le Spotter pour UNE collecte ; le plancher est le prix minimum facturé au client pour
              cette même collecte — il évite de vendre à perte. Une ligne sans sous-type vaut pour
              toute la catégorie ; une ligne avec sous-type la surcharge.
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["Catégorie", "Sous-type", "Récompense Spotter (Tips)", "Plancher client (F CFA)", "Actif", ""].map((t, i) => (
                      <TableCell key={i} sx={{
                        color: C.textMuted, borderColor: C.border, fontSize: 11,
                        textTransform: "uppercase", letterSpacing: "0.08em",
                      }}>{t}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grille.tarifsDeBase.map(t => (
                    <TableRow key={t.id}>
                      {/* Libellés lus au référentiel par identifiant (Z2-22) : un type
                          renommé s'affiche sous son nom courant. */}
                      <TableCell sx={{ color: C.text, borderColor: C.border, fontSize: 13 }}>
                        {categoriesRef.find(c => c.id === t.categorieId)?.libelle ?? t.categoryMain}
                        {t.categorieId == null && (
                          <span style={{ color: C.error, fontSize: 11 }}> · hors référentiel</span>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: t.categorySub ? C.accentLight : C.textMuted, borderColor: C.border, fontSize: 12 }}>
                        {t.sousTypeId != null
                          ? (sousTypes.find(s => s.id === t.sousTypeId)?.libelle ?? t.categorySub)
                          : (t.categorySub ?? "toute la catégorie")}
                      </TableCell>
                      {(["baseRewardTips", "baseCostCfa"] as const).map(champ => (
                        <TableCell key={champ} sx={{ borderColor: C.border }}>
                          <TextField
                            key={`${grille.ruleset.id}-${t.id}-${champ}-${t[champ]}`}
                            size="small" type="number" sx={{ ...champSx, width: 120 }}
                            disabled={figee} defaultValue={t[champ]}
                            error={tarifsVides.has(`${t.id}-${champ}`)}
                            helperText={tarifsVides.has(`${t.id}-${champ}`) ? "Obligatoire" : undefined}
                            onBlur={e => {
                              const cle = `${t.id}-${champ}`;
                              const v = parseInt(e.target.value);
                              /* Une case vide n'est jamais envoyée : le serveur la
                                 refuserait, et aucune valeur par défaut ne la remplace. */
                              if (e.target.value.trim() === "" || isNaN(v)) {
                                setTarifsVides(prev => new Set(prev).add(cle));
                                dispatch(openSnackbar(`${LIBELLE_CHAMP_TARIF[champ]} : champ obligatoire, rien n'est enregistré.`, "red"));
                                return;
                              }
                              setTarifsVides(prev => { const n = new Set(prev); n.delete(cle); return n; });
                              if (v !== t[champ]) {
                                agir(() => modifierTarif(t.id, { [champ]: v } as any), "Tarif enregistré.");
                              }
                            }}
                          />
                        </TableCell>
                      ))}
                      <TableCell sx={{ borderColor: C.border }}>
                        <Switch size="small" checked={t.actif} disabled={figee}
                          onChange={() => agir(() => modifierTarif(t.id, { actif: !t.actif }), "Tarif mis à jour.")} />
                      </TableCell>
                      <TableCell align="right" sx={{ borderColor: C.border }}>
                        {t.categorySub && !figee && (
                          <IconButton size="small" onClick={() => setTarifASupprimer(t)}>
                            <DeleteOutlineRoundedIcon sx={{ fontSize: 16, color: C.error }} />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {!figee && (
              <AjoutTarifFin
                categories={categoriesRef} sousTypes={sousTypes}
                onAjouter={(categorieId, sousTypeId, tips, cfa) =>
                  agir(() => creerTarifFin(grille.ruleset.id, categorieId, sousTypeId, tips, cfa), "Tarif fin ajouté.")}
              />
            )}
          </Box>

          {/* ── Simulateur ── */}
          <Box sx={{ background: C.card, border: `1px solid ${C.accentMuted}`, borderRadius: "8px", p: 2.5 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700, color: C.text, mb: 0.5 }}>
              Simulateur
            </Typography>
            <Typography sx={{ fontSize: 11, color: C.textMuted, mb: 2 }}>
              Calcule sur la grille affichée, active ou non. Rien n'est enregistré sur une quête ;
              la simulation est notée au journal du moteur, hors des estimations réelles.
            </Typography>

            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select value={sim.categoryMain} sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
                  onChange={e => setSim(s => ({ ...s, categoryMain: e.target.value, categorySub: "" }))}>
                  {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <Select value={sim.categorySub} displayEmpty sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
                  onChange={e => setSim(s => ({ ...s, categorySub: e.target.value }))}>
                  <MenuItem value="">Sous-type : aucun</MenuItem>
                  {sousTypes.filter(s => s.categorie?.libelle === sim.categoryMain)
                    .map(s => <MenuItem key={s.id} value={s.libelle}>{s.libelle}</MenuItem>)}
                </Select>
              </FormControl>
              {([["zone", zones], ["urgence", urgences], ["deficitInformation", deficits],
                 ["exclusiviteClient", exclusivites]] as const).map(([cle, options]) => (
                <FormControl key={cle} size="small" sx={{ minWidth: 190 }}>
                  <Select value={(sim as any)[cle]} displayEmpty
                    sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
                    onChange={e => setSim(s => ({ ...s, [cle]: e.target.value }))}>
                    {options.length === 0 && <MenuItem value="">aucun {cle}</MenuItem>}
                    {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                  </Select>
                </FormControl>
              ))}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Select value={sim.canal} displayEmpty
                  sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
                  onChange={e => setSim(s => ({ ...s, canal: e.target.value }))}>
                  <MenuItem value="">Canal : marge par défaut</MenuItem>
                  {(grille?.margesCanal ?? []).map(m => (
                    <MenuItem key={m.canal} value={m.canal}>
                      {m.canal} — {Math.round(m.marge * 100)} %
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField size="small" label="Collectes attendues" type="number" sx={{ ...champSx, width: 160 }}
                value={sim.collectesAttendues}
                onChange={e => setSim(s => ({ ...s, collectesAttendues: e.target.value }))} />
            </Box>

            <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap", mb: 2 }}>
              {CHAMPS.map(champ => {
                const on = sim.champs.includes(champ);
                return (
                  <Box key={champ}
                    onClick={() => setSim(s => ({
                      ...s, champs: on ? s.champs.filter(c => c !== champ) : [...s.champs, champ],
                    }))}
                    sx={{
                      px: "10px", py: "5px", borderRadius: "8px", cursor: "pointer", fontSize: 12,
                      background: on ? C.accentMuted : "rgba(31,30,27,0.04)",
                      color: on ? C.accentLight : C.textSub,
                      border: `1px solid ${on ? C.accent : "rgba(31,30,27,0.08)"}`,
                    }}>
                    {champ}
                  </Box>
                );
              })}
            </Box>

            {/* La grille simulée se lit AVANT de cliquer, pas après avoir lu
                le résultat : vingt minutes de simulation sur la mauvaise
                grille ne se rattrapent pas. */}
            <Box sx={{
              display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2,
              p: "10px 14px", borderRadius: "8px",
              background: figee ? "rgba(53,106,15,0.08)" : "rgba(124,86,0,0.08)",
              border: `1px solid ${figee ? "rgba(53,106,15,0.25)" : "rgba(124,86,0,0.25)"}`,
            }}>
              <Button variant="contained" size="small" onClick={lancerSimulation}
                sx={{ textTransform: "none", background: C.accent }}>
                Simuler
              </Button>
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                  {grille.ruleset.version}
                </Typography>
                <Typography sx={{ fontSize: 11, color: figee ? C.success : C.warning, fontWeight: 600 }}>
                  {figee
                    ? "Grille ACTIVE — c'est elle qui sert les estimations réelles"
                    : "BROUILLON — cette grille ne sert aucune estimation tant qu'elle n'est pas activée"}
                </Typography>
              </Box>
            </Box>

            {resultat && (
              <Box sx={{ background: C.card2, borderRadius: "8px", p: 2 }}>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 1.5 }}>
                  {[
                    ["Récompense Spotter", `${fr(resultat.recompenseSpotterTips)} Tips`,
                      `${fr(resultat.recompenseSpotterFcfaEquivalent)} F CFA par collecte`, C.success],
                    ["Prix client unitaire", `${fr(resultat.coutClientFcfaUnitaire)} F CFA`, "par collecte", C.text],
                    ["Prix client total", `${fr(resultat.coutClientFcfa)} F CFA`,
                      `${sim.collectesAttendues} collectes`, C.accentLight],
                    ["Marge Tribe", `${fr(resultat.margeFcfa)} F CFA`,
                      `${Math.round(resultat.margeEffectivePct * 100)} % visés`, C.warning],
                  ].map(([titre, valeur, sous, couleur]) => (
                    <Box key={titre as string}>
                      <Typography sx={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {titre}
                      </Typography>
                      <Typography sx={{ fontSize: 18, fontWeight: 700, color: couleur as string }}>{valeur}</Typography>
                      <Typography sx={{ fontSize: 11, color: C.textMuted }}>{sous}</Typography>
                    </Box>
                  ))}
                </Box>
                <Typography sx={{ fontSize: 10, color: C.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: "4px" }}>
                  Détail du calcul
                </Typography>
                {resultat.explication.map((ligne, i) => (
                  <Typography key={i} sx={{ fontSize: 11, color: C.textSub, lineHeight: 1.7 }}>· {ligne}</Typography>
                ))}
                <Typography sx={{
                  fontSize: 11, mt: 1, fontWeight: 600,
                  color: resultat.rulesetVersion === grille.ruleset.version ? C.textMuted : C.error,
                }}>
                  Calculé sur « {resultat.rulesetVersion} » · niveau de confiance {resultat.niveauConfiance}
                  {resultat.rulesetVersion !== grille.ruleset.version
                    && " — ATTENTION, ce n'est pas la grille affichée"}
                </Typography>
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
}

/** Ajout d'un tarif fin : une catégorie a déjà son tarif, on affine un sous-type. */
/* Création d'un tarif fin : catégorie et sous-type choisis au référentiel,
   envoyés par identifiant (Z2-18). */
function AjoutTarifFin({ categories, sousTypes, onAjouter }: {
  categories: TypeLieuCategorie[];
  sousTypes: TypeLieuSousType[];
  onAjouter: (categorieId: number, sousTypeId: number, tips: number, cfa: number) => void;
}) {
  const [cat, setCat] = useState<number | "">("");
  const [sous, setSous] = useState<number | "">("");
  /* Aucune valeur proposée d'office : 50 Tips et 250 F CFA partaient sans
     que personne les ait choisis. */
  const [tips, setTips] = useState("");
  const [cfa, setCfa] = useState("");
  const [vides, setVides] = useState<{ tips: boolean; cfa: boolean }>({ tips: false, cfa: false });
  const dispatch = useDispatch();

  const ajouter = () => {
    if (cat === "" || sous === "") return;
    const t = parseInt(tips);
    const c = parseInt(cfa);
    const manque = { tips: tips.trim() === "" || isNaN(t), cfa: cfa.trim() === "" || isNaN(c) };
    setVides(manque);
    if (manque.tips || manque.cfa) {
      const champs = [manque.tips && LIBELLE_CHAMP_TARIF.baseRewardTips, manque.cfa && LIBELLE_CHAMP_TARIF.baseCostCfa]
        .filter(Boolean).join(" et ");
      dispatch(openSnackbar(`À renseigner avant d'ajouter le tarif : ${champs}.`, "red"));
      return;
    }
    onAjouter(cat, sous, t, c);
  };

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mt: 2, pt: 2, borderTop: `1px dashed ${C.border}` }}>
      <FormControl size="small" sx={{ minWidth: 190 }}>
        <Select value={cat} displayEmpty sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
          onChange={e => { setCat(e.target.value === "" ? "" : Number(e.target.value)); setSous(""); }}>
          <MenuItem value="" disabled>Catégorie</MenuItem>
          {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.libelle}</MenuItem>)}
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: 190 }}>
        <Select value={sous} displayEmpty sx={{ ...champSx["& .MuiOutlinedInput-root"], fontSize: 13 }}
          onChange={e => setSous(e.target.value === "" ? "" : Number(e.target.value))}>
          <MenuItem value="" disabled>Sous-type à tarifer</MenuItem>
          {sousTypes.filter(s => s.categorie?.id === cat)
            .map(s => <MenuItem key={s.id} value={s.id}>{s.libelle}</MenuItem>)}
        </Select>
      </FormControl>
      <TextField size="small" label="Tips" type="number" sx={{ ...champSx, width: 110 }} required
        error={vides.tips} helperText={vides.tips ? "Obligatoire" : undefined}
        value={tips} onChange={e => { setTips(e.target.value); setVides(v => ({ ...v, tips: false })); }} />
      <TextField size="small" label="Plancher F CFA" type="number" sx={{ ...champSx, width: 150 }} required
        error={vides.cfa} helperText={vides.cfa ? "Obligatoire" : undefined}
        value={cfa} onChange={e => { setCfa(e.target.value); setVides(v => ({ ...v, cfa: false })); }} />
      <Button size="small" startIcon={<AddRoundedIcon />} disabled={cat === "" || sous === ""}
        onClick={ajouter}
        sx={{ textTransform: "none", color: C.accentLight }}>
        Tarifer ce sous-type
      </Button>
    </Box>
  );
}
